import React, { useState, useEffect, useRef } from 'react';
import { Upload, Bug, ShieldAlert, Zap, TrendingUp, Camera, Loader, AlertCircle, CheckCircle2, X, Sprout, Scan, BookOpen, Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import logo from '../assets/logo.png';
import img1 from '../assets/Picture1.png';
import img2 from '../assets/Picture2.png';
import img3 from '../assets/Picture3.png';

const PestDetection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const resultRef = useRef(null);

  useEffect(() => {
    const link = document.querySelector("link[rel='icon']");
    if (link) link.href = logo;
  }, []);

  const prompt = `You are an expert agricultural pest and plant disease specialist AI. Carefully examine the attached image and return ONLY a single JSON object (no extra text, no markdown, nothing else) matching this schema: \n \n { \n   "pest_name": "name or null", \n   "disease_name": "name or null", \n   "severity": "Low/Medium/High", \n   "symptoms": ["symptom1", "symptom2"], \n   "cause": "brief cause description or null", \n   "organic_solution": { \n     "pesticide": "common/trade name or null", \n     "dosage": "metric amount per liter (and optional US gal) or null", \n     "application": "short treatment steps" \n   }, \n   "chemical_solution": { \n     "pesticide": "common/trade name or null", \n     "dosage": "metric amount per liter (and optional US gal) or null", \n     "application": "short treatment steps" \n   }, \n   "prevention": ["concise tip1", "concise tip2", "concise tip3"], \n   "image_quality": "clear/unclear" \n } \n \n Rules: 1) If image unclear set image_quality to "unclear" and other fields null/empty; 2) Only diagnose with clear visual signs; 3) Keep fields concise; 4) Use real pesticide names and metric dosages; 5) Return valid JSON only.`;

  const handleFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(file);
        setImagePreview(reader.result);
        setResult(null);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearSelection = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const handleDownloadPDF = async () => {
    if (!pdfRef.current) return;
    setDownloadingPdf(true);
    try {
      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        windowWidth: 700 // narrower width to force tighter natural layout
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Calculate dimensions to fit exactly on one page while maintaining aspect ratio
      const imgProps = pdf.getImageProperties(imgData);
      const margin = 10;
      const availableWidth = pdfWidth - (margin * 2);
      const availableHeight = pdfHeight - (margin * 2);

      const ratio = Math.min(availableWidth / imgProps.width, availableHeight / imgProps.height);
      const finalWidth = imgProps.width * ratio;
      const finalHeight = imgProps.height * ratio;

      // Center horizontally
      const x = (pdfWidth - finalWidth) / 2;
      const y = margin; // Top margin

      pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);
      pdf.save(`AgriScan_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      console.error('PDF generation failed', e);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const pdfRef = useRef(null);

  const analyzeImage = async () => {
    if (!selectedImage) {
      setError('Please select an image first');
      return;
    }
    setAnalyzing(true);
    setError(null);
    setResult(null);
    try {
      const base64Image = imagePreview.split(',')[1];
      const response = await fetch('/api/gemini/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mime_type: selectedImage.type,
          data: base64Image,
          prompt,
        }),
      });
      const raw = await response.text();
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        const msg = raw?.trim() || 'Server returned non-JSON response.';
        setError(msg.startsWith('Proxy error') ? `Server proxy error: ${msg}` : `Server error: ${msg}`);
        return;
      }
      if (response.status === 429) {
        const wait = (data && data.retry_after_sec) ? Number(data.retry_after_sec) : 60;
        setError(`Quota exceed. ${wait}s baad retry ho raha hai...`);
        await new Promise(res => setTimeout(res, Math.max(1, wait) * 1000));
        const retryRes = await fetch('/api/gemini/analyze', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mime_type: selectedImage.type,
            data: base64Image,
            prompt,
          }),
        });
        const retryRaw = await retryRes.text();
        try {
          data = retryRaw ? JSON.parse(retryRaw) : null;
        } catch {
          setError('Retry failed. Please try again later.');
          return;
        }
        if (!retryRes.ok || (data && data.error)) {
          const msg = (data && data.error && data.error.message) ? data.error.message : (retryRaw?.trim() || 'request failed');
          setError(`Gemini error: ${msg}`);
          return;
        }
      } else if (!response.ok || (data && data.error)) {
        const msg = (data && data.error && data.error.message) ? data.error.message : (raw?.trim() || 'request failed');
        setError(`Gemini error: ${msg}`);
        return;
      }
      const candidate = Array.isArray(data.candidates) && data.candidates[0];
      let textBlock = null;
      if (candidate && candidate.content && Array.isArray(candidate.content.parts)) {
        for (const part of candidate.content.parts) {
          if (typeof part.text === 'string') {
            textBlock = part.text;
            break;
          }
        }
      }
      if (textBlock) {
        const jsonText = textBlock.replace(/```json\n?|\n?```/g, '').trim();
        try {
          const diagnosis = JSON.parse(jsonText);
          if (diagnosis.image_quality === 'unclear') {
            setError('Image quality is unclear. Please upload a clear, well-lit photo of the affected plant part.');
          } else {
            setResult(diagnosis);
          }
        } catch (parseErr) {
          setError('Model returned invalid JSON. Try another photo or retake with better lighting.');
        }
      } else {
        setError('Unable to analyze the image. Check server key or image size.');
      }
    } catch (e) {
      setError(e?.message ? `Failed: ${e.message}` : 'Failed to analyze image. Check network and server.');
    } finally {
      setAnalyzing(false);
    }
  };

  const getSeverityStyle = (sev) => {
    if (!sev) return 'bg-slate-100 text-slate-700 border-slate-200';
    const s = String(sev).toLowerCase();
    if (s === 'low') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    if (s === 'medium') return 'bg-amber-100 text-amber-800 border-amber-200';
    if (s === 'high') return 'bg-red-100 text-red-800 border-red-200';
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  return (
    <div className="min-h-screen bg-transparent">
      {/* Hidden PDF Template - Compact Version */}
      <div style={{ position: 'fixed', top: '-10000px', left: '-10000px', width: '700px', zIndex: -100 }}>
        <div ref={pdfRef} className="bg-white p-6 flex flex-col font-sans relative" style={{ minHeight: '900px' }}>
          {/* Header */}
          <div className="flex items-center justify-between border-b-2 border-emerald-500 pb-4 mb-4">
            <div className="flex items-center space-x-3">
              <img src={logo} alt="AgriScan Logo" className="w-12 h-12 object-contain" />
              <div>
                <h1 className="text-2xl font-black text-slate-800 tracking-tight">AgriScan AI</h1>
                <p className="text-emerald-600 font-bold uppercase tracking-wider text-xs">Diagnosis Report</p>
              </div>
            </div>
            <div className="text-right text-xs text-slate-500">
              <p>Date: {new Date().toLocaleDateString()}</p>
              <p>ID: #{Math.floor(Math.random() * 100000)}</p>
            </div>
          </div>

          {result && (
            <div className="flex-1 space-y-4">
              {/* Image Section - Compact */}
              <div className="flex gap-6 items-start">
                {imagePreview && (
                  <div className="w-1/3 h-40 bg-slate-50 rounded-xl overflow-hidden border border-slate-200 shadow-sm flex items-center justify-center">
                    <img src={imagePreview} alt="Scanned Plant" className="h-full w-full object-cover" />
                  </div>
                )}

                {/* Detected Issue Main Info */}
                <div className="flex-1 bg-emerald-50/50 rounded-xl p-4 border border-emerald-100 self-stretch flex flex-col justify-center">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-emerald-600 font-bold text-xs uppercase tracking-wider">Detected Issue</span>
                    <div className={`${getSeverityStyle(result.severity)} px-3 py-1 rounded-md font-bold uppercase text-xs scale-90 origin-right`}>
                      {result.severity}
                    </div>
                  </div>
                  <h2 className="text-2xl font-black text-slate-800 leading-tight">{result.pest_name || result.disease_name || 'Unidentified'}</h2>
                </div>
              </div>

              {/* Symptoms & Cause */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                  <h3 className="font-bold text-slate-800 text-sm mb-2 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2 text-blue-500" /> Symptoms
                  </h3>
                  <ul className="space-y-1">
                    {result.symptoms?.slice(0, 4).map((s, i) => (
                      <li key={i} className="flex items-start text-xs text-slate-600">
                        <div className="w-1 h-1 rounded-full bg-blue-400 mt-1.5 mr-2 shrink-0"></div>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                  <h3 className="font-bold text-slate-800 text-sm mb-2 flex items-center">
                    <Bug className="w-4 h-4 mr-2 text-amber-500" /> Cause
                  </h3>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    {result.cause || "No specific cause identified."}
                  </p>
                </div>
              </div>

              {/* Solutions */}
              <div>
                <h3 className="font-bold text-slate-800 text-sm border-b border-slate-200 pb-1 mb-3">Treatment Plan</h3>

                <div className="grid grid-cols-2 gap-4">
                  {result.organic_solution && (
                    <div className="bg-white border border-emerald-200 rounded-lg p-3 shadow-sm">
                      <h4 className="flex items-center text-emerald-700 font-bold text-sm mb-2">
                        <Sprout className="w-4 h-4 mr-2" /> Organic
                      </h4>
                      <div className="space-y-1 text-xs">
                        <p><span className="font-semibold text-slate-600">Product:</span> {result.organic_solution.pesticide}</p>
                        <p><span className="font-semibold text-slate-600">Dosage:</span> {result.organic_solution.dosage}</p>
                        <p className="mt-1"><span className="bg-emerald-50 text-[10px] px-1.5 py-0.5 rounded text-emerald-800">{result.organic_solution.application}</span></p>
                      </div>
                    </div>
                  )}

                  {result.chemical_solution && (
                    <div className="bg-white border border-blue-200 rounded-lg p-3 shadow-sm">
                      <h4 className="flex items-center text-blue-700 font-bold text-sm mb-2">
                        <Zap className="w-4 h-4 mr-2" /> Chemical
                      </h4>
                      <div className="space-y-1 text-xs">
                        <p><span className="font-semibold text-slate-600">Product:</span> {result.chemical_solution.pesticide}</p>
                        <p><span className="font-semibold text-slate-600">Dosage:</span> {result.chemical_solution.dosage}</p>
                        <p className="mt-1"><span className="bg-blue-50 text-[10px] px-1.5 py-0.5 rounded text-blue-800">{result.chemical_solution.application}</span></p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Prevention */}
              {result.prevention?.length > 0 && (
                <div className="bg-indigo-50/50 rounded-xl p-4 border border-indigo-100">
                  <h3 className="font-bold text-indigo-900 text-sm mb-2 flex items-center">
                    <ShieldAlert className="w-4 h-4 mr-2" /> Prevention
                  </h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {result.prevention.slice(0, 4).map((tip, i) => (
                      <div key={i} className="flex items-start text-xs text-indigo-800">
                        <CheckCircle2 className="w-3 h-3 mr-1.5 mt-0.5 text-indigo-500 shrink-0" />
                        {tip}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="mt-auto border-t border-slate-200 pt-3 flex items-center justify-between text-slate-400 text-xs">
            <div className="font-medium">AgriScan AI Solutions</div>
            <div className="text-right flex items-center space-x-4">
              <span>+91 9661063589</span>
              <span>ss9375527@gmail.com</span>
            </div>
          </div>

          {/* Watermark/Footer Pattern */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500"></div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative pt-12 pb-20 px-4 sm:px-6 lg:px-8 text-center">
        <h1 className="text-4xl md:text-5xl font-black text-slate-800 mb-4 tracking-tight">
          AI Pest & Disease <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">Detection</span>
        </h1>
        <p className="max-w-2xl mx-auto text-lg text-slate-600">
          Instantly identify crop diseases and pests with our advanced AI model. Get actionable solutions and protect your yield.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* Upload Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-xl p-6 border border-white/50">
              <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                <Upload className="w-5 h-5 mr-2 text-emerald-600" />
                Upload Plant Image
              </h2>

              {!imagePreview ? (
                <div
                  className="border-3 border-dashed border-slate-200 rounded-2xl h-80 flex flex-col items-center justify-center bg-slate-50/50 hover:bg-emerald-50/30 hover:border-emerald-300 transition-all cursor-pointer group"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg shadow-emerald-900/5 mb-4 group-hover:scale-110 transition-transform">
                    <Bug className="w-10 h-10 text-emerald-500" />
                  </div>
                  <p className="text-slate-600 font-semibold text-lg">Click to Upload</p>
                  <p className="text-slate-400 text-sm mt-1">or drag and drop</p>
                  <div className="mt-6 flex space-x-3">
                    <button onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }} className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition shadow-lg shadow-emerald-200">Select File</button>
                    <button onClick={(e) => { e.stopPropagation(); cameraInputRef.current?.click(); }} className="px-4 py-2 bg-blue-500 text-white rounded-xl text-sm font-medium hover:bg-blue-600 transition shadow-lg shadow-blue-200 flex items-center">
                      <Camera className="w-4 h-4 mr-1.5" /> Camera
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative rounded-2xl overflow-hidden shadow-lg group">
                  <img src={imagePreview} alt="Selected" className="w-full h-80 object-cover" />
                  <button onClick={clearSelection} className="absolute top-3 right-3 p-1.5 bg-black/50 text-white rounded-full hover:bg-red-500 transition backdrop-blur-sm">
                    <X className="w-5 h-5" />
                  </button>
                  {!analyzing && !result && (
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      <p className="text-white font-semibold flex items-center bg-black/50 px-4 py-2 rounded-full backdrop-blur-sm"><Upload className="w-4 h-4 mr-2" /> Change Image</p>
                    </div>
                  )}
                </div>
              )}

              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />

              <div className="mt-6">
                <button
                  onClick={analyzeImage}
                  disabled={!selectedImage || analyzing}
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-4 rounded-xl font-bold text-lg hover:from-emerald-700 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl hover:shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 active:scale-95"
                >
                  {analyzing ? (
                    <>
                      <Loader className="w-6 h-6 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <ShieldAlert className="w-6 h-6" />
                      <span>Diagnose Now</span>
                    </>
                  )}
                </button>
              </div>

              {error && (
                <div className="mt-4 bg-red-50 border border-red-100 p-4 rounded-xl flex items-start animate-fade-in">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-red-700 text-sm font-medium">{error}</p>
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div className="lg:col-span-3">
            {!result && !analyzing ? (
              <div className="h-full bg-white/60 backdrop-blur-md rounded-3xl border border-dashed border-slate-300 flex flex-col items-center justify-center p-12 text-center min-h-[400px]">
                <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mb-6">
                  <TrendingUp className="w-10 h-10 text-slate-300" />
                </div>
                <h3 className="text-xl font-bold text-slate-700 mb-2">Ready to Analyze</h3>
                <p className="text-slate-500 max-w-sm">Upload a photo to see detailed pest identification, symptoms, and treatment recommendations here.</p>
              </div>
            ) : analyzing ? (
              <div className="h-full bg-white/80 backdrop-blur-md rounded-3xl border border-white/50 flex flex-col items-center justify-center p-12 text-center min-h-[400px] shadow-xl">
                <div className="relative w-24 h-24 mb-8">
                  <div className="absolute inset-0 border-4 border-slate-100 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-emerald-500 rounded-full border-t-transparent animate-spin"></div>
                  <Bug className="absolute inset-0 m-auto w-8 h-8 text-emerald-500 animate-pulse" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-2">Analyzing Image...</h3>
                <p className="text-slate-500">Our AI is examining the plant for potential threats.</p>
              </div>
            ) : (
              <div ref={resultRef} className="space-y-6 animate-fade-in-up">
                {/* Header with Download Button for Result */}
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-black text-slate-800">Diagnosis Results</h3>
                  <button
                    onClick={handleDownloadPDF}
                    disabled={downloadingPdf}
                    className="flex items-center space-x-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-xl font-bold hover:bg-emerald-200 transition active:scale-95 disabled:opacity-50"
                  >
                    {downloadingPdf ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    <span>{downloadingPdf ? 'Generating PDF...' : 'Download Report'}</span>
                  </button>
                </div>

                {/* Main Diagnosis Card */}
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100">
                  <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-6 text-white flex justify-between items-start">
                    <div>
                      <div className="text-emerald-100 font-medium text-sm mb-1 uppercase tracking-wider">Detected Issue</div>
                      <h2 className="text-3xl font-black">{result.pest_name || result.disease_name || 'Healthy / Unidentified'}</h2>
                    </div>
                    <div className={`px-4 py-1.5 rounded-full text-sm font-bold bg-white/20 border border-white/30 backdrop-blur-md capitalize`}>
                      {result.severity} Severity
                    </div>
                  </div>

                  <div className="p-8">
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-bold text-slate-800 mb-4 flex items-center"><AlertCircle className="w-5 h-5 mr-2 text-blue-500" />Symptoms</h4>
                        <ul className="space-y-2">
                          {result.symptoms?.map((s, i) => (
                            <li key={i} className="flex items-start text-sm text-slate-600 bg-slate-50 p-2 rounded-lg">
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 mr-2 shrink-0"></div>
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-800 mb-4 flex items-center"><Bug className="w-5 h-5 mr-2 text-slate-500" />Cause</h4>
                        <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-100">
                          {result.cause || "No specific cause identified."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Solutions Grid */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Organic */}
                  {result.organic_solution && (
                    <div className="bg-white rounded-3xl shadow-lg p-6 border-l-4 border-emerald-500">
                      <h3 className="font-bold text-lg text-slate-800 mb-4 flex items-center">
                        <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center mr-3"><Leaf className="w-5 h-5 text-emerald-600" /></span>
                        Organic Solution
                      </h3>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between border-b border-dashed border-slate-100 pb-2">
                          <span className="text-slate-500">Treatment</span>
                          <span className="font-medium text-slate-900 text-right">{result.organic_solution.pesticide}</span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-slate-100 pb-2">
                          <span className="text-slate-500">Dosage</span>
                          <span className="font-medium text-slate-900 text-right">{result.organic_solution.dosage}</span>
                        </div>
                        <div className="pt-1">
                          <span className="text-slate-500 block mb-1">Application</span>
                          <p className="text-slate-700 bg-emerald-50/50 p-2 rounded-lg">{result.organic_solution.application}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Chemical */}
                  {result.chemical_solution && (
                    <div className="bg-white rounded-3xl shadow-lg p-6 border-l-4 border-blue-500">
                      <h3 className="font-bold text-lg text-slate-800 mb-4 flex items-center">
                        <span className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center mr-3"><Zap className="w-5 h-5 text-blue-600" /></span>
                        Chemical Solution
                      </h3>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between border-b border-dashed border-slate-100 pb-2">
                          <span className="text-slate-500">Treatment</span>
                          <span className="font-medium text-slate-900 text-right">{result.chemical_solution.pesticide}</span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-slate-100 pb-2">
                          <span className="text-slate-500">Dosage</span>
                          <span className="font-medium text-slate-900 text-right">{result.chemical_solution.dosage}</span>
                        </div>
                        <div className="pt-1">
                          <span className="text-slate-500 block mb-1">Application</span>
                          <p className="text-slate-700 bg-blue-50/50 p-2 rounded-lg">{result.chemical_solution.application}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Prevention */}
                {result.prevention?.length > 0 && (
                  <div className="bg-indigo-50/50 rounded-3xl p-8 border border-indigo-100">
                    <h3 className="font-bold text-lg text-indigo-900 mb-4 flex items-center">
                      <ShieldAlert className="w-5 h-5 mr-3 text-indigo-600" /> Prevention Tips
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {result.prevention.map((tip, i) => (
                        <div key={i} className="bg-white p-4 rounded-xl shadow-sm flex items-start">
                          <CheckCircle2 className="w-5 h-5 text-indigo-500 mr-3 shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-700 font-medium">{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            )}
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-black text-slate-800 mb-4">How AgriScan Works</h2>
          <p className="text-slate-600 max-w-2xl mx-auto">Three simple steps to protect your crops and ensure a healthy harvest.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative bg-white ring-1 ring-slate-200 rounded-2xl p-6 h-full flex flex-col items-center text-center">
              <div className="mb-6 relative overflow-hidden rounded-xl shadow-lg w-full h-48">
                <img src={img1} alt="Capture" className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm p-2 rounded-lg">
                  <Camera className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">1. Snap a Photo</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Take a clear picture of the affected plant part using your phone's camera. Ensure good lighting for best results.
              </p>
            </div>
          </div>

          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative bg-white ring-1 ring-slate-200 rounded-2xl p-6 h-full flex flex-col items-center text-center">
              <div className="mb-6 relative overflow-hidden rounded-xl shadow-lg w-full h-48">
                <img src={img2} alt="Analyze" className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm p-2 rounded-lg">
                  <Scan className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">2. AI Analysis</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Our advanced AI model instantly scans the image to identify pests, diseases, or nutrient deficiencies with high accuracy.
              </p>
            </div>
          </div>

          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative bg-white ring-1 ring-slate-200 rounded-2xl p-6 h-full flex flex-col items-center text-center">
              <div className="mb-6 relative overflow-hidden rounded-xl shadow-lg w-full h-48">
                <img src={img3} alt="Solution" className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm p-2 rounded-lg">
                  <BookOpen className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">3. Get Solutions</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Receive immediate, actionable treatment recommendations, including organic and chemical options to save your crop.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper components for icon consistency
const Leaf = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" /><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" /></svg>
)

export default PestDetection;
